import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Car, User, MapPin, FileText, Calendar, Phone, Mail, 
  Shield, CheckCircle, ChevronRight, ChevronLeft, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import api from '../services/api';

const DriverApplicationPage = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [applicationRef, setApplicationRef] = useState('');
  
  const [formData, setFormData] = useState({
    // Personal Info
    applicant_name: '',
    applicant_email: '',
    applicant_phone: '',
    date_of_birth: '',
    
    // Address
    street_address: '',
    city: '',
    state: 'NC',
    zip_code: '',
    
    // License
    drivers_license_number: '',
    drivers_license_state: 'NC',
    drivers_license_expiry: '',
    
    // Vehicle
    vehicle_info: {
      make: '',
      model: '',
      year: new Date().getFullYear(),
      color: '',
      license_plate: '',
      vehicle_type: 'sedan',
      is_wheelchair_accessible: false, // Note: We are not wheelchair accessible
      passenger_capacity: 4
    },
    
    // Experience
    years_driving: 0,
    has_rideshare_experience: false,
    previous_rideshare_platforms: [],
    
    // Background
    has_criminal_record: false,
    criminal_record_explanation: '',
    has_accidents_violations: false,
    accidents_violations_explanation: '',
    
    // Agreements
    agreed_to_background_check: false,
    agreed_to_vehicle_inspection: false,
    agreed_to_terms: false,
    signature: ''
  });

  const handleChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.agreed_to_background_check || !formData.agreed_to_vehicle_inspection || !formData.agreed_to_terms) {
      toast.error('Please agree to all required terms');
      return;
    }
    
    if (!formData.signature) {
      toast.error('Please provide your electronic signature');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post('/drivers/apply', formData);
      setApplicationRef(response.data.reference);
      setSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    }
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[var(--background)] flex items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="card rounded-2xl p-8 max-w-md w-full text-center"
        >
          <div className="w-20 h-20 mx-auto mb-6 bg-green-500/20 rounded-full flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-green-400" />
          </div>
          <h2 className="text-2xl font-bold mb-2 text-white">Application Submitted!</h2>
          <p className="text-[var(--text-muted)] mb-4">Your reference number is:</p>
          <p className="text-3xl font-mono font-bold text-[#D4AF37] mb-6">{applicationRef}</p>
          <p className="text-sm text-[var(--text-muted)] mb-8">
            We'll review your application and contact you within 2-3 business days. 
            Please save your reference number for future inquiries.
          </p>
          <Link to="/">
            <Button className="w-full">Return to Home</Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black py-12 px-4">
      {/* Header */}
      <div className="max-w-3xl mx-auto mb-8">
        <Link to="/" className="text-[#D4AF37] hover:text-[#F4C2C2] flex items-center gap-2 mb-6">
          <ChevronLeft className="w-5 h-5" />
          Back to Home
        </Link>
        
        <div className="text-center">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-14 h-14 bg-[#D4AF37] rounded-xl flex items-center justify-center">
              <Car className="w-8 h-8 text-black" />
            </div>
            <h1 className="text-3xl font-bold text-white font-['Cormorant_Garamond']">
              Drive with NC Help Hub
            </h1>
          </div>
          <p className="text-gray-400 max-w-xl mx-auto">
            Join our team of professional drivers providing reliable transportation services in North Carolina
          </p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="max-w-3xl mx-auto mb-8">
        <div className="flex justify-between items-center">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex-1 flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                step >= s ? 'bg-[#D4AF37] text-black' : 'bg-gray-700 text-gray-400'
              }`}>
                {s}
              </div>
              {s < 4 && (
                <div className={`flex-1 h-1 mx-2 ${step > s ? 'bg-[#D4AF37]' : 'bg-gray-700'}`}></div>
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-2 text-xs text-gray-400">
          <span>Personal</span>
          <span>Vehicle</span>
          <span>Background</span>
          <span>Agreements</span>
        </div>
      </div>

      {/* Form Card */}
      <motion.div 
        className="max-w-3xl mx-auto card rounded-2xl shadow-xl overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="p-8">
          {/* Step 1: Personal Information */}
          {step === 1 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold flex items-center gap-2 text-white">
                <User className="w-5 h-5 text-[#D4AF37]" />
                Personal Information
              </h2>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1 text-[var(--text-muted)]">Full Name *</label>
                  <Input
                    value={formData.applicant_name}
                    onChange={(e) => handleChange('applicant_name', e.target.value)}
                    placeholder="John Doe"
                    data-testid="driver-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-[var(--text-muted)]">Date of Birth *</label>
                  <Input
                    type="date"
                    value={formData.date_of_birth}
                    onChange={(e) => handleChange('date_of_birth', e.target.value)}
                    data-testid="driver-dob-input"
                    className="text-gray-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-[var(--text-muted)]">Email *</label>
                  <Input
                    type="email"
                    value={formData.applicant_email}
                    onChange={(e) => handleChange('applicant_email', e.target.value)}
                    placeholder="john@example.com"
                    data-testid="driver-email-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-[var(--text-muted)]">Phone *</label>
                  <Input
                    type="tel"
                    value={formData.applicant_phone}
                    onChange={(e) => handleChange('applicant_phone', e.target.value)}
                    placeholder="(555) 123-4567"
                    data-testid="driver-phone-input"
                  />
                </div>
              </div>
              
              <h3 className="font-semibold mt-6 flex items-center gap-2 text-white">
                <MapPin className="w-4 h-4 text-[#D4AF37]" />
                Address
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1 text-[var(--text-muted)]">Street Address *</label>
                  <Input
                    value={formData.street_address}
                    onChange={(e) => handleChange('street_address', e.target.value)}
                    placeholder="123 Main Street"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-[var(--text-muted)]">City *</label>
                  <Input
                    value={formData.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                    placeholder="Raleigh"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium mb-1 text-[var(--text-muted)]">State</label>
                    <Input value="NC" disabled />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-[var(--text-muted)]">ZIP *</label>
                    <Input
                      value={formData.zip_code}
                      onChange={(e) => handleChange('zip_code', e.target.value)}
                      placeholder="27601"
                    />
                  </div>
                </div>
              </div>
              
              <h3 className="font-semibold mt-6 flex items-center gap-2 text-white">
                <FileText className="w-4 h-4 text-[#D4AF37]" />
                Driver's License
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1 text-[var(--text-muted)]">License Number *</label>
                  <Input
                    value={formData.drivers_license_number}
                    onChange={(e) => handleChange('drivers_license_number', e.target.value)}
                    placeholder="License #"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-[var(--text-muted)]">State</label>
                  <Input value="NC" disabled />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-[var(--text-muted)]">Expiry Date *</label>
                  <Input
                    type="date"
                    value={formData.drivers_license_expiry}
                    onChange={(e) => handleChange('drivers_license_expiry', e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Vehicle Information */}
          {step === 2 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold flex items-center gap-2 text-white">
                <Car className="w-5 h-5 text-[#D4AF37]" />
                Vehicle Information
              </h2>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1 text-[var(--text-muted)]">Make *</label>
                  <Input
                    value={formData.vehicle_info.make}
                    onChange={(e) => handleChange('vehicle_info.make', e.target.value)}
                    placeholder="Toyota"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-[var(--text-muted)]">Model *</label>
                  <Input
                    value={formData.vehicle_info.model}
                    onChange={(e) => handleChange('vehicle_info.model', e.target.value)}
                    placeholder="Camry"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-[var(--text-muted)]">Year *</label>
                  <Input
                    type="number"
                    min="2015"
                    max={new Date().getFullYear() + 1}
                    value={formData.vehicle_info.year}
                    onChange={(e) => handleChange('vehicle_info.year', parseInt(e.target.value))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-[var(--text-muted)]">Color *</label>
                  <Input
                    value={formData.vehicle_info.color}
                    onChange={(e) => handleChange('vehicle_info.color', e.target.value)}
                    placeholder="Silver"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-[var(--text-muted)]">License Plate *</label>
                  <Input
                    value={formData.vehicle_info.license_plate}
                    onChange={(e) => handleChange('vehicle_info.license_plate', e.target.value)}
                    placeholder="ABC-1234"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-[var(--text-muted)]">Vehicle Type *</label>
                  <select
                    className="w-full p-2 border rounded-lg"
                    value={formData.vehicle_info.vehicle_type}
                    onChange={(e) => handleChange('vehicle_info.vehicle_type', e.target.value)}
                    data-testid="vehicle-type-select"
                  >
                    <option value="sedan">Sedan</option>
                    <option value="suv">SUV</option>
                    <option value="van">Van</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-[var(--text-muted)]">Passenger Capacity</label>
                  <Input
                    type="number"
                    min="1"
                    max="15"
                    value={formData.vehicle_info.passenger_capacity}
                    onChange={(e) => handleChange('vehicle_info.passenger_capacity', parseInt(e.target.value))}
                  />
                </div>
              </div>
              
              <h3 className="font-semibold mt-6 text-white">Driving Experience</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1 text-[var(--text-muted)]">Years Driving</label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.years_driving}
                    onChange={(e) => handleChange('years_driving', parseInt(e.target.value))}
                  />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <input
                    type="checkbox"
                    id="rideshare"
                    checked={formData.has_rideshare_experience}
                    onChange={(e) => handleChange('has_rideshare_experience', e.target.checked)}
                    className="w-5 h-5 accent-[#D4AF37]"
                  />
                  <label htmlFor="rideshare" className="text-sm">Previous rideshare experience</label>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Background */}
          {step === 3 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold flex items-center gap-2 text-white">
                <Shield className="w-5 h-5 text-[#D4AF37]" />
                Background Information
              </h2>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
                <AlertCircle className="w-5 h-5 inline mr-2" />
                Please answer honestly. Having a record does not automatically disqualify you.
              </div>
              
              <div className="space-y-4">
                <div className="p-4 border rounded-lg">
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={formData.has_criminal_record}
                      onChange={(e) => handleChange('has_criminal_record', e.target.checked)}
                      className="w-5 h-5 accent-[#D4AF37]"
                    />
                    <span>I have a criminal record</span>
                  </label>
                  {formData.has_criminal_record && (
                    <div className="mt-3">
                      <label className="block text-sm font-medium mb-1 text-[var(--text-muted)]">Please explain:</label>
                      <textarea
                        className="w-full p-3 border rounded-lg"
                        rows="3"
                        value={formData.criminal_record_explanation}
                        onChange={(e) => handleChange('criminal_record_explanation', e.target.value)}
                        placeholder="Provide details about your record..."
                      />
                    </div>
                  )}
                </div>
                
                <div className="p-4 border rounded-lg">
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={formData.has_accidents_violations}
                      onChange={(e) => handleChange('has_accidents_violations', e.target.checked)}
                      className="w-5 h-5 accent-[#D4AF37]"
                    />
                    <span>I have had traffic accidents or violations in the last 3 years</span>
                  </label>
                  {formData.has_accidents_violations && (
                    <div className="mt-3">
                      <label className="block text-sm font-medium mb-1 text-[var(--text-muted)]">Please explain:</label>
                      <textarea
                        className="w-full p-3 border rounded-lg"
                        rows="3"
                        value={formData.accidents_violations_explanation}
                        onChange={(e) => handleChange('accidents_violations_explanation', e.target.value)}
                        placeholder="Describe the incidents..."
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Agreements */}
          {step === 4 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold flex items-center gap-2 text-white">
                <CheckCircle className="w-5 h-5 text-[#D4AF37]" />
                Agreements & Signature
              </h2>
              
              <div className="space-y-4">
                <label className="flex items-start gap-3 p-4 border rounded-lg cursor-pointer hover:bg-[var(--background-subtle)]">
                  <input
                    type="checkbox"
                    checked={formData.agreed_to_background_check}
                    onChange={(e) => handleChange('agreed_to_background_check', e.target.checked)}
                    className="w-5 h-5 mt-1 accent-[#D4AF37]"
                    data-testid="agree-background-check"
                  />
                  <div>
                    <span className="font-medium">Background Check Authorization *</span>
                    <p className="text-sm text-gray-500 mt-1">
                      I authorize NC Help Hub to conduct a background check including criminal history, 
                      driving record, and employment verification.
                    </p>
                  </div>
                </label>
                
                <label className="flex items-start gap-3 p-4 border rounded-lg cursor-pointer hover:bg-[var(--background-subtle)]">
                  <input
                    type="checkbox"
                    checked={formData.agreed_to_vehicle_inspection}
                    onChange={(e) => handleChange('agreed_to_vehicle_inspection', e.target.checked)}
                    className="w-5 h-5 mt-1 accent-[#D4AF37]"
                    data-testid="agree-vehicle-inspection"
                  />
                  <div>
                    <span className="font-medium">Vehicle Inspection Agreement *</span>
                    <p className="text-sm text-gray-500 mt-1">
                      I agree to complete a vehicle inspection and maintain my vehicle in safe, 
                      clean condition suitable for passenger transport.
                    </p>
                  </div>
                </label>
                
                <label className="flex items-start gap-3 p-4 border rounded-lg cursor-pointer hover:bg-[var(--background-subtle)]">
                  <input
                    type="checkbox"
                    checked={formData.agreed_to_terms}
                    onChange={(e) => handleChange('agreed_to_terms', e.target.checked)}
                    className="w-5 h-5 mt-1 accent-[#D4AF37]"
                    data-testid="agree-terms"
                  />
                  <div>
                    <span className="font-medium">Terms of Service *</span>
                    <p className="text-sm text-gray-500 mt-1">
                      I agree to the NC Help Hub Driver Terms of Service and Privacy Policy. 
                      I understand this is a contract for independent contractor services.
                    </p>
                  </div>
                </label>
              </div>
              
              <div className="pt-6 border-t">
                <label className="block text-sm font-medium mb-2">Electronic Signature *</label>
                <Input
                  value={formData.signature}
                  onChange={(e) => handleChange('signature', e.target.value)}
                  placeholder="Type your full legal name"
                  className="text-lg"
                  data-testid="driver-signature-input"
                />
                <p className="text-xs text-gray-500 mt-2">
                  By typing your name, you agree this constitutes your legal signature.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="px-8 py-6 bg-[var(--background-subtle)] border-t border-[var(--border-primary)] flex justify-between">
          <Button
            variant="outline"
            onClick={() => setStep(s => s - 1)}
            disabled={step === 1}
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>
          
          {step < 4 ? (
            <Button onClick={() => setStep(s => s + 1)} data-testid="next-step-btn">
              Next
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit} 
              disabled={loading}
              className="bg-green-600 hover:bg-green-700"
              data-testid="submit-application-btn"
            >
              {loading ? 'Submitting...' : 'Submit Application'}
            </Button>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default DriverApplicationPage;
